import { GMXNavigation } from './primary-nav'
import { loadHtmlPartialScripts } from '../../../utils/loadHtmlPartialScripts'
import { swapAuthLinks } from './auth-link-swap'

/**
 * Nav link panel — a nested, links-only sub-menu that opens at nav-menu width
 * (e.g. Shop -> Build & Price, Inventory). It mirrors gmx-nav-flyout's loading
 * and open/close behaviour but is sized like the gmx-navigation-submenu rather than
 * the wide content flyout.
 *
 * Trigger wiring: by default the panel binds to a sibling `.nav-btn` in its
 * parent (the Shop case). When `data-managed="true"` the host component drives
 * open/close via the public methods (the account case), so the panel skips
 * auto-wiring and trigger `.active` bookkeeping.
 */
export class GMXNavLinkPanel extends HTMLElement {
    parentNavItem: HTMLElement
    hamburgerMenuCloseBtn: HTMLButtonElement
    hamburgerMenuContainer: HTMLElement
    panelCollapseBtn: HTMLButtonElement | null
    panelCloseBtn: HTMLButtonElement | null
    GMXNavigation: GMXNavigation
    preloaded: boolean
    managed: boolean

    private initialized = false
    private pendingOpen = false

    private parentNavItemClick = this._handleParentNavItemClick.bind(this)
    private parentNavItemKeydown = this._handleParentNavItemKeydown.bind(this)
    private panelCollapseBtnClick = this._handlePanelCollapseBtnClick.bind(this)
    private panelCloseBtnClick = this._handlePanelCloseBtnClick.bind(this)

    connectedCallback(): void {
        this.preloaded = this.dataset.preloaded === 'true'
        this.managed = this.dataset.managed === 'true'

        if (this.preloaded || this.dataset.contentPath) {
            if (this.preloaded) {
                this.initPanel()
            } else {
                try {
                    fetch(this.dataset.contentPath, {priority: "low"})
                        .then((response) => {
                            if (response.ok) {
                                response.text()
                                    .then((html) => {
                                        this.innerHTML = html
                                        loadHtmlPartialScripts(this, 'low');

                                        this.initPanel()
                                        swapAuthLinks()
                                    })
                            } else {
                                window.console.error(`ERROR:  Failed to load link panel. response: ${response.statusText}`)
                                this._insertErrorLoadingMarkup()
                            }
                        }).catch(error => {
                        window.console.error(`ERROR:  Failed to load link panel: ${error}`)
                        this._insertErrorLoadingMarkup()
                    })
                } catch (error) {
                    window.console.error(`ERROR:  Failed to load link panel: ${error}`)
                    this._insertErrorLoadingMarkup()
                }
            }
        } else {
            this.parentNavItem = this.parentElement.querySelector('.nav-btn');
            window.console.error(`ERROR:  No content path or content found for link panel for ` + (this.parentNavItem?.innerHTML ?? ''))
            this._insertErrorLoadingMarkup()
        }
    }

    private _insertErrorLoadingMarkup(): void {
        this.innerHTML = `
            <div class="flyout-top">
                <button class="nav-small-button flyout-collapse-btn" aria-label="back"></button>
                <button class="nav-small-button gmx-btn gmx-btn-secondary flyout-close-btn" aria-label="close link panel" data-icon-name="close"></button>
            </div>
            <div class="flyout-loading-error-wrapper">
                <p>Sorry, there was an error loading this content.</p>
                <button class="flyout-retry-button gmx-btn gmx-btn-primary">Retry</button>
            </div>
        `
        this.initPanel()
        const retryButton = this.querySelector('.flyout-retry-button')
        retryButton.addEventListener('click', () => {
            this.connectedCallback()
        })
    }

    private initPanel(): void {
        this.GMXNavigation = this.closest('gmx-navigation') as GMXNavigation;

        this.hamburgerMenuCloseBtn = this.GMXNavigation.querySelector('button.close-btn');
        this.hamburgerMenuContainer = this.GMXNavigation.querySelector('.hamburger-submenu-container');
        // Detach before re-binding so a re-init (reconnect or retry) never
        // stacks duplicate handlers on these in-panel buttons.
        this.panelCloseBtn = this.querySelector<HTMLButtonElement>('.flyout-close-btn');
        if (this.panelCloseBtn) {
            this.panelCloseBtn.removeEventListener('click', this.panelCloseBtnClick);
            this.panelCloseBtn.addEventListener('click', this.panelCloseBtnClick);
        }
        this.panelCollapseBtn = this.querySelector<HTMLButtonElement>('.flyout-collapse-btn');
        if (this.panelCollapseBtn) {
            this.panelCollapseBtn.removeEventListener('click', this.panelCollapseBtnClick);
            this.panelCollapseBtn.addEventListener('click', this.panelCollapseBtnClick);
        }

        // The trigger is a sibling of this element (not a child), so unlike the
        // in-panel buttons it survives a re-init (e.g. retry after a failed
        // fetch). Detach any previous binding before re-binding so retries and
        // re-initialisation never stack duplicate handlers on it.
        if (this.parentNavItem) {
            this.parentNavItem.removeEventListener('click', this.parentNavItemClick);
            this.parentNavItem.removeEventListener('keydown', this.parentNavItemKeydown);
        }
        this.parentNavItem = this.parentElement.querySelector('.nav-btn');
        if (!this.managed && this.parentNavItem) {
            this.parentNavItem.addEventListener('click', this.parentNavItemClick);
            this.parentNavItem.addEventListener('keydown', this.parentNavItemKeydown);
        }

        // A top-bar link can be clicked while this panel is still fetching, so honour an
        // open requested before there was anything to open.
        this.initialized = true;
        if (this.pendingOpen) {
            this.pendingOpen = false;
            this.open();
        }
    }

    disconnectedCallback(): void {
        // Detach every handler so nothing survives the panel if it is later
        // reconnected. The trigger is the important one (it lives outside this
        // element), but the in-panel buttons are cleaned up here too.
        // Drop any deferred open too, so a fetch that lands after removal can't
        // reveal this panel once it is reconnected.
        this.pendingOpen = false;
        this.panelCloseBtn?.removeEventListener('click', this.panelCloseBtnClick);
        this.panelCollapseBtn?.removeEventListener('click', this.panelCollapseBtnClick);
        if (this.parentNavItem) {
            this.parentNavItem.removeEventListener('click', this.parentNavItemClick);
            this.parentNavItem.removeEventListener('keydown', this.parentNavItemKeydown);
        }
    }

    private _handleParentNavItemClick(e: MouseEvent): void {
        // The trigger is an anchor pointing at the parent page. Let modified clicks
        // (cmd/ctrl/shift) and non-primary buttons perform the anchor's native
        // behaviour so the parent page opens in a new tab; otherwise open the panel.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
            return;
        }
        e.preventDefault();
        this.toggle();
    }

    private _handleParentNavItemKeydown(e: KeyboardEvent): void {
        // Enter fires a native click (handled above); intercept Space so it opens
        // the panel instead of scrolling the page, matching the old button.
        if (e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            this.toggle();
        }
    }

    public toggle(): void {
        if (this.classList.contains('open')) {
            this.collapse();
        } else {
            this.open();
        }
    }

    /** Drops an open requested before init, so a late fetch can't reveal this panel behind a closed menu. */
    public cancelPendingOpen(): void {
        this.pendingOpen = false;
    }

    public open(): void {
        if (!this.initialized) {
            this.pendingOpen = true;
            return;
        }
        this.GMXNavigation.setBackgroundHandlerInactive();
        this.GMXNavigation.querySelectorAll('gmx-nav-flyout, gmx-nav-link-panel').forEach((panel) => panel.classList.remove('open'));
        // Opening any panel deactivates every trigger so no stale `.active`
        // marker is left on a previously-open panel's trigger. Managed panels
        // (account) re-mark their own trigger via _syncPanelState, so only the
        // add is skipped for them.
        this.GMXNavigation.querySelectorAll('.nav-btn, .nav-top-link').forEach((item) => item.classList.remove('active'));
        if (!this.managed) {
            this._triggers().forEach((trigger) => trigger.classList.add('active'));
        }
        this.classList.remove('close');
        this.classList.add('open');
        this.hamburgerMenuCloseBtn.classList.add('flyout-open');
        // Focus into the panel on open.
        this.panelCloseBtn?.focus();
        this._animateLinks();
    }

    public collapse(): void {
        if (!this.managed) {
            this._triggers().forEach((trigger) => trigger.classList.remove('active'));
        }
        this.classList.remove('open');
        this.classList.add('close');
        this.hamburgerMenuCloseBtn.classList.remove('flyout-open');
        this.GMXNavigation.setBackgroundHandlerActive();
        this._removeLinkAnimation();
    }

    /**
     * Everything that opens this panel: the menu item beside it, plus the top-bar link
     * mirroring that item, if the author asked for one. Both carry `.active` together so
     * neither is left marked open once the other closes the panel.
     */
    private _triggers(): HTMLElement[] {
        const triggers = this.parentNavItem ? [this.parentNavItem] : []
        const navRef = this.dataset.navRef
        if (navRef) {
            triggers.push(
                ...this.GMXNavigation.querySelectorAll<HTMLElement>(`.nav-top-link[data-nav-ref="${navRef}"]`)
            )
        }
        return triggers
    }

    public close(): void {
        this.collapse();
    }

    /**
     * Stagger the link reveal on open to match the nav menu
     * (GMXNavigation._handleHamburgerMenuClick). Reset + force a reflow first so
     * the animation replays each time the panel is opened.
     */
    private _animateLinks(): void {
        const links = this.querySelectorAll<HTMLElement>('.nav-btn');
        links.forEach((link) => link.classList.remove('animated'));
        void this.offsetWidth;
        links.forEach((link, index) =>
            setTimeout(() => {
                link.classList.add('animated');
            }, index * 100)
        );
    }

    private _removeLinkAnimation(): void {
        this.querySelectorAll('.nav-btn').forEach((link) => link.classList.remove('animated'));
    }

    private _handlePanelCollapseBtnClick(): void {
        this.collapse();
        // Return focus to the visible trigger on collapse (account btn if managed).
        const trigger = this.parentElement?.querySelector<HTMLElement>('.nav-btn:not(.hidden)');
        trigger?.focus();
    }

    private _handlePanelCloseBtnClick(): void {
        // Closing the menu collapses this panel; collapsing it again here would leave the
        // background handler active over a menu that is no longer open.
        this.GMXNavigation.hamburgerMenuClose();
    }
}
if (!customElements.get('gmx-nav-link-panel')) {
    customElements.define('gmx-nav-link-panel', GMXNavLinkPanel)
}
